'use client';

import { useEffect, useState, type ReactElement } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type ServiceStatus =
  | 'operational'
  | 'degraded'
  | 'down'
  | 'maintenance'
  | string;

interface Service {
  id: number;
  name: string;
  category: string;
  description: string;
  status: ServiceStatus;
  uptime: number;
  latency_ms: number;
  last_checked_at: string;
}

interface StatusResponse {
  page: {
    name: string;
    updated_at: string;
    last_checked_at: string;
    server_location: string;
  };
  overall_status: ServiceStatus;
  services: Service[];
}

const STATUS_SHORT: Record<string, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  down: 'Outage',
  maintenance: 'Maintenance',
};

const STATUS_DOT: Record<string, string> = {
  operational: 'bg-green-500',
  degraded: 'bg-amber-400',
  down: 'bg-red-500',
  maintenance: 'bg-blue-400',
};

type BadgeTone = 'operational' | 'degraded' | 'down' | 'maintenance';

interface BadgeSummary {
  tone: BadgeTone;
  dot: string;
  text: string;
  longLabel: string;
  shortLabel: string;
  dialogTitle: string;
  affectedCount: number;
  downCount: number;
  degradedCount: number;
  maintenanceCount: number;
}

function countByStatus(services: Service[]): {
  downCount: number;
  degradedCount: number;
  maintenanceCount: number;
} {
  const normalized = services.map((service) =>
    String(service.status || '').toLowerCase()
  );

  return {
    downCount: normalized.filter((status) => status === 'down').length,
    degradedCount: normalized.filter((status) => status === 'degraded').length,
    maintenanceCount: normalized.filter((status) => status === 'maintenance')
      .length,
  };
}

function pluralizeServices(count: number): string {
  return count === 1 ? '1 service' : `${count} services`;
}

function summarizeStatus(data: StatusResponse): BadgeSummary {
  const services = data.services || [];
  const { downCount, degradedCount, maintenanceCount } =
    countByStatus(services);
  const affectedCount = downCount + degradedCount + maintenanceCount;

  if (affectedCount === 0) {
    return {
      tone: 'operational',
      dot: STATUS_DOT.operational,
      text: 'text-green-500',
      longLabel: 'All systems operational',
      shortLabel: 'Operational',
      dialogTitle: 'All systems operational',
      affectedCount,
      downCount,
      degradedCount,
      maintenanceCount,
    };
  }

  if (downCount >= 2) {
    return {
      tone: 'down',
      dot: STATUS_DOT.down,
      text: 'text-red-500',
      longLabel: `Major interruption · ${pluralizeServices(downCount)} down`,
      shortLabel: 'Major outage',
      dialogTitle: 'Major interruption',
      affectedCount,
      downCount,
      degradedCount,
      maintenanceCount,
    };
  }

  if (downCount === 1) {
    return {
      tone: 'down',
      dot: STATUS_DOT.down,
      text: 'text-red-500',
      longLabel:
        degradedCount + maintenanceCount > 0
          ? `Service interruption · ${pluralizeServices(affectedCount)} affected`
          : 'Service interruption · 1 service down',
      shortLabel: 'Outage',
      dialogTitle: 'Service interruption',
      affectedCount,
      downCount,
      degradedCount,
      maintenanceCount,
    };
  }

  if (degradedCount === 0 && maintenanceCount > 0) {
    return {
      tone: 'maintenance',
      dot: STATUS_DOT.maintenance,
      text: 'text-blue-500',
      longLabel:
        maintenanceCount === 1
          ? 'Maintenance in progress'
          : `Maintenance · ${maintenanceCount} services`,
      shortLabel: 'Maintenance',
      dialogTitle: 'Scheduled maintenance',
      affectedCount,
      downCount,
      degradedCount,
      maintenanceCount,
    };
  }

  if (degradedCount >= 2) {
    return {
      tone: 'degraded',
      dot: STATUS_DOT.degraded,
      text: 'text-amber-500',
      longLabel: `Multiple services degraded · ${degradedCount} affected`,
      shortLabel: 'Degraded',
      dialogTitle: 'Multiple services degraded',
      affectedCount,
      downCount,
      degradedCount,
      maintenanceCount,
    };
  }

  return {
    tone: 'degraded',
    dot: STATUS_DOT.degraded,
    text: 'text-amber-500',
    longLabel: 'Degraded performance · 1 service affected',
    shortLabel: 'Degraded',
    dialogTitle: 'Degraded performance',
    affectedCount,
    downCount,
    degradedCount,
    maintenanceCount,
  };
}

function formatLatency(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatRelative(iso: string): string {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (deltaSec < 60) return `${deltaSec}s ago`;
    const min = Math.floor(deltaSec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
  } catch {
    return '—';
  }
}

function useStatus(): StatusResponse | null {
  const [data, setData] = useState<StatusResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await fetch('https://status.1ani.me/api/status', {
          headers: { accept: 'application/json' },
        });
        if (!res.ok) return;
        const json = (await res.json()) as StatusResponse;
        if (!cancelled) setData(json);
      } catch {
        /* network blip — keep previous data */
      }
    };
    load();
    const interval = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return data;
}

function ServiceRow({ service }: { service: Service }): ReactElement {
  const dot = STATUS_DOT[service.status] || 'bg-gray-400';
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-background/40 p-3">
      <span className={cn('mt-1.5 h-2 w-2 flex-shrink-0 rounded-full', dot)} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-semibold text-foreground">
            {service.name}
          </p>
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {STATUS_SHORT[service.status] || service.status}
          </span>
        </div>
        {service.description ? (
          <p className="line-clamp-2 text-xs text-muted-foreground">
            {service.description}
          </p>
        ) : null}
        <div className="mt-1.5 flex items-center gap-2 text-[11px] tabular-nums text-muted-foreground">
          <span>{service.uptime.toFixed(1)}% uptime</span>
          <span>·</span>
          <span>{formatLatency(service.latency_ms)}</span>
          <span>·</span>
          <span>checked {formatRelative(service.last_checked_at)}</span>
        </div>
      </div>
    </div>
  );
}

export function SiteStatusBadge(): ReactElement | null {
  const data = useStatus();
  if (!data) return null;

  const summary = summarizeStatus(data);
  const isOperational = summary.tone === 'operational';

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={summary.longLabel}
          title={summary.longLabel}
          className="inline-flex max-w-[180px] items-center gap-1.5 rounded-full border border-border/60 bg-card/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-border hover:bg-card hover:text-foreground sm:max-w-none sm:px-2.5 sm:text-xs"
        >
          <span className="relative flex h-2 w-2 flex-shrink-0">
            {isOperational ? null : (
              <span
                className={cn(
                  'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60',
                  summary.dot
                )}
              />
            )}
            <span
              className={cn(
                'relative inline-flex h-2 w-2 rounded-full',
                summary.dot
              )}
            />
          </span>
          <span className="truncate sm:hidden">{summary.shortLabel}</span>
          <span className="hidden truncate sm:inline">{summary.longLabel}</span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={cn('h-2 w-2 rounded-full', summary.dot)} />
            <span className={summary.text}>{summary.dialogTitle}</span>
          </DialogTitle>
          <DialogDescription>
            {summary.affectedCount === 0
              ? `${pluralizeServices(data.services.length)} reporting normal.`
              : `${pluralizeServices(summary.affectedCount)} affected · `}
            Last checked {formatRelative(data.page.last_checked_at)}
            {data.page.server_location
              ? ` · monitored from ${data.page.server_location}`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {data.services.map((service) => (
            <ServiceRow key={service.id} service={service} />
          ))}
        </div>
        <div className="pt-2 text-center">
          <a
            href="https://status.1ani.me"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Open full status page →
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
